import { QueryStatus } from "../../app/types";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus,
  setFeatureFlags,
  setFeatureFlagsError,
  setFeatureFlagsStatus
} from "./firebaseSlice";
import { getFirebaseApp } from "./firebaseApp";
import {
  getAll,
  getValue,
  getRemoteConfig,
  fetchAndActivate
} from "firebase/remote-config";
import {
  parseRemoteMessage,
  RemoteMessage
} from "../MessageQueue/messageParser";

// Set to 60 seconds (1 minute) for all environments to reduce network call frequency
const MESSAGE_FETCH_INTERVAL_MS = 60_000; // 60 seconds
const FLAG_FETCH_INTERVAL_MS = 3_600_000; // 1 hour (unchanged)

export type FirebaseFlags = {
  stats_feature_enabled: boolean;
  stats_feature_enabled_ABS: boolean;
  use_dynamic_reports: boolean;
  user_ccc_reports_enabled: boolean;
  enable_milestone_ccc: boolean;
  web_assessments_enabled: boolean;
  allow_bulk_invite: boolean;
  use_completeAssessmentRequest_lambda: boolean;
  activity_notes_visible: boolean;
  // Shared kill-switch for the in-app survey pilot (iOS/Android/web read the
  // same key). OFF by default; flipped per-env in Firebase Remote Config.
  survey_pilot_enabled: boolean;
  // Refiner IDs from Firebase Remote Config. The project id is SHARED with
  // iOS/Android (refiner_project_id), but Refiner has a separate web-channel
  // form, so web reads its own refiner_form_uuid_web and leaves the mobile
  // refiner_form_uuid untouched. Empty when unset → no survey is shown
  // (launchSurvey guards on empty ids).
  refiner_project_id: string;
  refiner_form_uuid_web: string;
};

export const defaultFlags: FirebaseFlags = {
  stats_feature_enabled: false,
  stats_feature_enabled_ABS: false,
  use_dynamic_reports: false,
  user_ccc_reports_enabled: false,
  enable_milestone_ccc: false,
  web_assessments_enabled: false,
  allow_bulk_invite: false,
  use_completeAssessmentRequest_lambda: false,
  activity_notes_visible: true,
  survey_pilot_enabled: false,
  refiner_project_id: "",
  refiner_form_uuid_web: ""
};

export interface UseFirebaseFeatureFlagsReturn {
  flags: FirebaseFlags;
  status: QueryStatus;
  error?: string;
  getFeatureFlags: () => void;
}

export const fetchRemoteMessages = async (): Promise<RemoteMessage[]> => {
  const remoteConfig = getRemoteConfig(getFirebaseApp());
  remoteConfig.settings.minimumFetchIntervalMillis = MESSAGE_FETCH_INTERVAL_MS;

  await fetchAndActivate(remoteConfig);
  const allEntries = getAll(remoteConfig);

  const messageEntries = Object.entries(allEntries).filter(([key]) =>
    key.startsWith("message_queue_")
  );

  const parsedMessages: RemoteMessage[] = [];

  messageEntries.forEach(([key, value]) => {
    const raw = value.asString();
    const parsed = parseRemoteMessage(key, raw);
    //console.log("[messageQueue] remote message", { key, raw, parsed });
    if (parsed) {
      parsedMessages.push(parsed);
    }
  });

  return parsedMessages;
};

export const useFirebaseFeatureFlags = (): UseFirebaseFeatureFlagsReturn => {
  const flags = useAppSelector(selectFeatureFlags);
  const status = useAppSelector(selectFeatureFlagsStatus);
  const error = useAppSelector(selectFeatureFlagsError);

  const dispatch = useAppDispatch();

  const getFeatureFlags = () => {
    const app = getFirebaseApp();
    const remoteConfig = getRemoteConfig(app);

    remoteConfig.settings.minimumFetchIntervalMillis = FLAG_FETCH_INTERVAL_MS;

    dispatch(setFeatureFlagsStatus("loading"));
    dispatch(setFeatureFlagsError());

    fetchAndActivate(remoteConfig)
      .then(() => {
        const remoteFlags: FirebaseFlags = {
          stats_feature_enabled: getValue(
            remoteConfig,
            "stats_feature_enabled"
          ).asBoolean(),
          stats_feature_enabled_ABS: getValue(
            remoteConfig,
            "stats_feature_enabled_ABS"
          ).asBoolean(),
          use_dynamic_reports: getValue(
            remoteConfig,
            "use_dynamic_reports"
          ).asBoolean(),
          user_ccc_reports_enabled: getValue(
            remoteConfig,
            "user_ccc_reports_enabled"
          ).asBoolean(),
          enable_milestone_ccc: getValue(
            remoteConfig,
            "enable_milestone_ccc"
          ).asBoolean(),
          web_assessments_enabled: getValue(
            remoteConfig,
            "web_assessments_enabled"
          ).asBoolean(),
          allow_bulk_invite: getValue(
            remoteConfig,
            "allow_bulk_invite"
          ).asBoolean(),
          use_completeAssessmentRequest_lambda: getValue(
            remoteConfig,
            "use_completeAssessmentRequest_lambda"
          ).asBoolean(),
          activity_notes_visible: getValue(
            remoteConfig,
            "activity_notes_visible"
          ).asBoolean(),
          survey_pilot_enabled: getValue(
            remoteConfig,
            "survey_pilot_enabled"
          ).asBoolean(),
          // Project id shared with mobile; web-only form uuid (Refiner serves a
          // separate web-channel form). Empty when unset → no survey.
          refiner_project_id: getValue(
            remoteConfig,
            "refiner_project_id"
          ).asString(),
          refiner_form_uuid_web: getValue(
            remoteConfig,
            "refiner_form_uuid_web"
          ).asString()
        };
        dispatch(setFeatureFlagsStatus("succeeded"));
        dispatch(setFeatureFlags(remoteFlags));
      })
      .catch((error) => {
        console.error("failed to fetch remote config:", error);
        dispatch(
          setFeatureFlagsError(
            `Error fetching remote config: ${JSON.stringify(error)}`
          )
        );
      });
  };

  return {
    flags,
    status,
    error,
    getFeatureFlags
  };
};

